import React, { useEffect, useState } from "react"
import { format, isBefore, isAfter, parse } from 'date-fns';
import { utils, writeFileXLSX } from 'xlsx';
import CustomDialog from "../MasterDialog"
import DatePickerMonitoring from "components/DatePickers/DatePickerMonitoring/DatePickerMonitoring";
import { Box, Button, Divider, FormControl, Grid, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { AppColors } from "resources/AppColors";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "redux/reducers/notificationReducer";
import { CommonTexts, studiesTexts } from "locale/es";
import { initializeFormAnswersExport } from "redux/reducers/studyFormAnswersExportsReducer";

export default function DownloadDialog({
  title,
  show,
  handleShow,
  tableData,
  formName,
}) {
  const [minDate, setMinDate] = useState(null);
  const [initialDate, setInitialDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  // * If value is 0 it sets export data type as value, if 1 is set as label
  const [typeData, setTypeData] = useState(1)
  const [currentFormId, setCurrentFormId] = useState(undefined)

  const { data: answersData, loading: answersLoading, error: answersError } = useSelector(({ formAnswersExport }) => formAnswersExport)

  const selectedFormId = useSelector(({ selectedForm }) => selectedForm)
  const selectedStudyId = useSelector(({ study }) => study.data?.id)

  const dispatch = useDispatch()

  useEffect(() => {
    if (tableData.answers.length > 0 && !!selectedFormId && !answersLoading && selectedFormId !== currentFormId && selectedStudyId) {
      setCurrentFormId(selectedFormId)
      dispatch(initializeFormAnswersExport(selectedStudyId, selectedFormId))
      return
    }
  }, [selectedFormId, selectedStudyId, tableData])

  useEffect(() => {
    if (answersData.answers.length > 0) {
      const firstSubmission = answersData.answers.reduce((minDate, currentDataPoint) => {
        const dataPointDate = parse(currentDataPoint.submitted_at.answer, 'dd-MM-yyyy HH:mm', new Date())
        return isBefore(dataPointDate, minDate) ? dataPointDate : minDate
      }, new Date())

      setMinDate(firstSubmission)
    }
  }, [answersData?.answers])

  //Function that change the type of the data
  const handleChange = (event) => {
    setTypeData(event.target.value);
  };

  function handleInitialDate(e) {
    setInitialDate(e);
  }

  function handleEndDate(e) {
    setEndDate(e)
  }

  /*
   * It takes the data from the API and creates an array of objects with the data that will be used to
   * create the excel file
   * 
   *  1 - Formats the data from the table to a xlsx valid format
   *  2 - If a date range is selected (it is possible to use only start or end date) it filters out the data outside of the range
   *  3 - User can select two types of exports labels and values
  */

  function handleExportingData() {
    const dataFilteredByDateRange = answersData.answers.reduce((allData, currentDataPoint) => {
      const dataPointDate = new Date(format(parse(currentDataPoint.submitted_at.answer, 'dd-MM-yyyy HH:mm', new Date()), "yyyy-MM-dd"))
      const selectedInitialDate = initialDate && new Date(format(initialDate, "yyyy-MM-dd"))
      const selectedEndDate = endDate && new Date(format(endDate, "yyyy-MM-dd"))

      if (initialDate && isBefore(dataPointDate, selectedInitialDate)) return allData

      if (endDate && isAfter(dataPointDate, selectedEndDate)) return allData

      return [...allData, currentDataPoint]
    }, []).reverse()

    const formatDataForExport = dataFilteredByDateRange.map((currentDataPoint) =>
      Object.keys(currentDataPoint).reduce((formattedDataPoints, questionName) => {
        if (typeof currentDataPoint[questionName] === "object" && currentDataPoint[questionName].label !== undefined && currentDataPoint[questionName].value !== undefined) {
          formattedDataPoints[questionName] = typeData === 0 ? currentDataPoint[questionName].value.toString() : currentDataPoint[questionName].label.toString()
        } else {
          formattedDataPoints[questionName] = currentDataPoint[questionName].answer;
        }
        return formattedDataPoints;
      }, {})
    );

    if (formatDataForExport.length === 0) {
      dispatch(toast(studiesTexts.EXPORT_NO_DATA, "error"))
      return
    }

    const worksheet = utils.json_to_sheet(formatDataForExport);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "answers");

    const currentDate = format(new Date(), 'dd/MM/yyyy');

    const fileName = `${formName}_${typeData === 0 ? "values" : "labels"}_${currentDate}.xlsx`

    writeFileXLSX(workbook, fileName, { compression: true })
  }

  return (
    <CustomDialog
      title={title}
      show={show}
      handleShow={handleShow}
    >
      <Divider />
      <Box px={4} py={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body" >
            {CommonTexts.DATE_RANGE}
          </Typography>
          <FormControl margin='normal' sx={{ width: "152px" }}>
            <DatePickerMonitoring
              value={initialDate}
              setValue={handleInitialDate}
              label={CommonTexts.FROM}
              minDate={minDate}
              maxDate={new Date()}
              name='birthdate'
            />
          </FormControl>
          <FormControl margin='normal' sx={{ width: "152px" }}>
            <DatePickerMonitoring
              value={endDate}
              setValue={handleEndDate}
              label={CommonTexts.UNTIL}
              minDate={initialDate ? initialDate : minDate}
              maxDate={new Date()}
              name='birthdate'
            />
          </FormControl>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" >
          <Typography variant="body" >
            {CommonTexts.EXPORT_TYPE}
          </Typography>
          <FormControl variant="outlined" margin='normal'>
            <InputLabel>{CommonTexts.TYPES}</InputLabel>
            <Select
              value={typeData}
              onChange={handleChange}
              label={CommonTexts.TYPES}
              sx={{ minWidth: 367 }}
            >
              <MenuItem sx={{ color: AppColors.PRIMARY }}
                value={1}>{CommonTexts.LABELS}</MenuItem>
              <MenuItem sx={{ color: AppColors.PRIMARY }}
                value={0}>{CommonTexts.VALUES}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Divider />
        <Box textAlign="right" mt={1}>
          <Button
            variant="contained"
            onClick={handleExportingData}
            sx={{ bgcolor: AppColors.PRIMARY }}>
            {CommonTexts.DOWNLOAD}
          </Button>
        </Box>
      </Box>
    </CustomDialog>
  )
}
